import { useState, useRef } from 'react';
import useSWR from 'swr';
import _ from 'lodash';
import { type MondayClient } from '@gorilla/common/src/lib/monday-api/common';
import { type Board, getBoard } from '@gorilla/common/src/lib/monday-api/api';
import { Box, Button, Dropdown, Flex, Icon, Loader } from '@vibe/core';
import { dropdownValue } from './dropdownValue';
import { AppModal, AppModalContent } from './AppModal';
import { diagnoseTests, AllTests, type DiagnoseTest } from '@gorilla/common/src/lib/diagnose';
import { Board as BoardIcon, Download } from '@vibe/icons';

const allDiagnoseTests = [AllTests, ...diagnoseTests];

const DIAGNOSE_TEST_OPTIONS = allDiagnoseTests.map((diagnoseTest) => ({
  label: diagnoseTest.name,
  value: diagnoseTest.id,
}));

function executeDiagnoseTest(diagnoseTest: DiagnoseTest, board: Board, mondayClient: MondayClient, onUpdate: () => void) {
  const abortController = new AbortController();
  let state: 'running' | 'aborted' | 'errored' | 'finished' = 'running';
  const log: { message: string; type: string }[] = [];
  let result: any;

  diagnoseTest
    .run(
      mondayClient,
      {
        boardId: board.id,
        abortSignal: abortController.signal,
      },
      (message, type) => {
        log.push({ message, type });
        onUpdate();
      },
    )
    .then(
      (res) => {
        result = res;
        state = 'finished';
        onUpdate();
      },
      (err) => {
        if (abortController.signal) {
          state = 'aborted';
        } else {
          console.error('Error running diagnose test:', err);
          state = 'errored';
        }

        onUpdate();
      },
    );

  return {
    getState: () => state,

    getLog: () => log,

    getResult: () => result,

    abort: () => {
      abortController.abort();
    },
  };
}

function useExecuteDiagnoseTest() {
  const [_update, setUpdate] = useState<number>(0);
  const executeTestRef = useRef<ReturnType<typeof executeDiagnoseTest> | null>(null);

  return {
    getExecuteTest: () => {
      return executeTestRef.current;
    },

    executeDiagnoseTest: (diagnoseTest: DiagnoseTest, board: Board, mondayClient: MondayClient) => {
      const executeTest = executeDiagnoseTest(diagnoseTest, board, mondayClient, () => {
        setUpdate((prev: number) => prev + 1);
      });

      executeTestRef.current = executeTest;

      setUpdate((prev: number) => prev + 1);

      return executeTest;
    },
  };
}

type BoardDiagnoseModalProps = {
  mondayClient: MondayClient;
  boardId: number;
  onClose: () => void;
};

export function BoardDiagnoseModal({ boardId, onClose, mondayClient }: BoardDiagnoseModalProps) {
  const [selectedDiagnoseTestId, setSelectedDiagnoseTestId] = useState<string>(allDiagnoseTests[0].id);
  const { executeDiagnoseTest, getExecuteTest } = useExecuteDiagnoseTest();
  const executeTest = getExecuteTest();
  const isDiagnoseTestRunning = executeTest?.getState() === 'running';
  const diagnoseTestLog = executeTest?.getLog() || [];
  const diagnoseTestResult = executeTest?.getResult();

  const {
    data: board,
    isLoading,
    error,
  } = useSWR<Board>(`/diagnose-board/${boardId}`, () => getBoard(mondayClient, boardId), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });

  const selectedDiagnoseTestOption =
    DIAGNOSE_TEST_OPTIONS.find((option) => option.value === selectedDiagnoseTestId) || DIAGNOSE_TEST_OPTIONS[0];
  const selectedDiagnoseTest = allDiagnoseTests.find((test) => test.id === selectedDiagnoseTestId)!;

  let content;

  if (error) {
    console.error('Error fetching board:', error);
    content = <div>Error loading board data.</div>;
  } else if (isLoading || !board) {
    content = (
      <Flex direction="column" justify="start" align="start" gap={16}>
        <Flex direction="row" justify="start" align="center" gap={10} style={{ marginTop: '-3px', marginBottom: '5px' }}>
          <Loader size="xs" />
          <div style={{ margin: 0 }}>Loading</div>
        </Flex>
      </Flex>
    );
  } else {
    content = (
      <Flex direction="column" justify="start" align="start" gap={16}>
        <Flex direction="row" justify="start" align="center" gap={8} style={{ marginTop: '-3px' }}>
          <Icon icon={BoardIcon} size={24} />
          <h4 style={{ margin: 0 }}>{`Diagnose board "${board.name}"`}</h4>
        </Flex>
        <div
          style={{
            width: '100%',
          }}
        >
          <Flex gap={16}>
            <div
              style={{
                flex: 1,
              }}
            >
              <Dropdown
                searchable
                disabled={isDiagnoseTestRunning}
                size="medium"
                options={DIAGNOSE_TEST_OPTIONS}
                value={dropdownValue(selectedDiagnoseTestOption)}
                placeholder="Choose a test"
                clearable={false}
                onChange={(option) => {
                  setSelectedDiagnoseTestId(option.value);
                }}
              />
            </div>
            <Button
              kind={isDiagnoseTestRunning ? 'secondary' : 'primary'}
              size="medium"
              disabled={isDiagnoseTestRunning}
              onClick={() => {
                executeDiagnoseTest(selectedDiagnoseTest, board, mondayClient);
              }}
            >
              Start diagnosis
            </Button>
          </Flex>
          {/* 
          <div>
            <h3>{selectedDiagnoseTest.name}</h3>
            <p>{selectedDiagnoseTest.description}</p>
          </div>
          */}
        </div>

        {diagnoseTestLog.length > 0 ? (
          <div
            style={{
              width: 'calc(100% - 2px)',
              maxWidth: 'calc(850px - 2px)',
              backgroundColor: '#f6f7fb',
              border: '1px solid #dcdfec',
              borderRadius: '4px',
              borderBottomRightRadius: diagnoseTestResult ? '0' : '4px',
              borderBottomLeftRadius: diagnoseTestResult ? '0' : '4px',
            }}
          >
            <div
              style={{
                overflow: 'auto',
                padding: '24px',
                maxHeight: '200px',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {diagnoseTestLog.map((logEntry: any, index: number) => (
                <div key={index}>&gt; {logEntry.message}&nbsp;&nbsp;&nbsp;</div>
              ))}
            </div>
          </div>
        ) : null}

        {diagnoseTestResult ? (
          <div
            style={{
              marginTop: '-16px',
              padding: '20px 20px 20px 24px',
              width: 'calc(100% - 46px)',
              backgroundColor: '#f6f7fb',
              border: '1px solid #dcdfec',
              borderTop: 'none',
              borderBottomRightRadius: '4px',
              borderBottomLeftRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <Flex direction="row" justify="start" align="center" gap={16}>
              <div
                style={{
                  flex: 1,
                  fontWeight: '600',
                }}
              >
                {diagnoseTestResult.message}
              </div>
              <Button
                leftIcon={Download}
                kind="tertiary"
                size="small"
                color="primary"
                onClick={() => {
                  const report = diagnoseTestResult.report || {};
                  report.board = board;
                  const blob = new Blob([JSON.stringify(diagnoseTestResult, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${selectedDiagnoseTest.id}_${board.id}_result.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                Download report
              </Button>
            </Flex>
          </div>
        ) : null}
      </Flex>
    );
  }

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      data-testid="board-diagnose-modal"
      //width={'full-width'}
      width={850}
      show={true}
      onClose={() => {
        if (executeTest) {
          executeTest.abort();
        }
        onClose();
      }}
    >
      <AppModalContent>
        <Box marginBottom="small">{content}</Box>
      </AppModalContent>
    </AppModal>
  );
}
